import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useEffect, useState } from "react";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useToast } from "@hooks/use-toast";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useAppParams } from "@router/router-helper";
import { CompanyId } from "@custom-types/base-types";
import { SubscriptionStatus } from "@custom-types/subscriptions-types";

interface Props {
  /** Whether the user can view product details */
  canViewProductDetails: boolean;

  /** The subscription status of the company */
  subscriptionStatus: Extract<
    SubscriptionStatus,
    "subscription-ended" | "trial-ended"
  >;
}

interface ReturnProps {
  /** The status of company retention offer product */
  canApplyRetentionOffer: boolean;

  /** The subtitle text to display on the page */
  subtitle: string;

  /** The retention period in days */
  retentionOfferDuration: number | null;

  /** Callback when user requests 14 day retention offer */
  onRetentionRequested: () => Promise<void>;

  /** Whether the data is being fetched or not */
  isLoading: boolean;
}

/** Helper function to manage subscription expired */
export function useSubscriptionExpiredPage({
  canViewProductDetails,
  subscriptionStatus,
}: Props): ReturnProps {
  const { navigateToProjects } = useAppNavigation();
  const { showToast } = useToast();
  const { handleErrorWithToast } = useErrorContext();
  const { companyId } = useAppParams();

  const [canApplyRetentionOffer, setCanApplyRetentionOffer] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [retentionOfferDuration, setRetentionOfferDuration] = useState<
    number | null
  >(null);

  /** Fetch active company products and update subscription condition */
  useEffect(() => {
    async function getActiveCompanyProducts(
      companyId: CompanyId
    ): Promise<void> {
      setIsLoading(true);

      try {
        const activeCompanyProducts =
          await faroCoreApiClient.company.getActiveCompanyProducts({
            companyId,
          });

        const companyRetentionOffer = activeCompanyProducts?.find(
          (product) => product.identifier === "retention-offer"
        );

        setCanApplyRetentionOffer(
          !!companyRetentionOffer?.availability.isAvailable
        );

        const companyRetentionOfferDuration =
          companyRetentionOffer?.expirationDays ?? null;

        /** Retention offer duration should not be null */
        if (companyRetentionOfferDuration === null) {
          throw new Error("Retention offer duration is missing");
        }

        setRetentionOfferDuration(companyRetentionOfferDuration);
      } catch (error) {
        // Handle error directly here in order to show specific info about the field that failed: thumbnailUrl
        // Otherwise the error will be handled by the error slice, but that will show a generic message without details
        handleErrorWithToast({
          id: `SubscriptionExpired-getActiveCompanyProducts-${Date.now().toString()}`,
          title: "Could not fetch company products",
          error,
        });
      } finally {
        setIsLoading(false);
      }
    }

    if (companyId) {
      void getActiveCompanyProducts(companyId);
    }
  }, [companyId, handleErrorWithToast]);

  function getSubtitle(): string {
    if (subscriptionStatus === "subscription-ended") {
      if (canApplyRetentionOffer) {
        return canViewProductDetails
          ? "Your subscription has ended. To keep your workspace active, we're offering a 14-day extension with Enterprise features. Renew or upgrade anytime during this period."
          : "The subscription has ended. Please contact your admin to restore access.";
      } else {
        return canViewProductDetails
          ? `Your ${retentionOfferDuration}-day trial has ended. Renew or upgrade your plan to continue working on your projects, or contact us for more information`
          : "This trial has ended. Please contact your admin to restore access.";
      }
    } else if (subscriptionStatus === "trial-ended") {
      return canViewProductDetails
        ? "Your trial has ended. Upgrade your plan to continue working on your project, or contact us for more information."
        : "This trial has ended. Please contact your admin to restore access.";
    }

    return "";
  }

  /** Callback when user requests 14 day retention offer */
  async function onRetentionRequested(): Promise<void> {
    if (companyId) {
      try {
        await faroCoreApiClient.company.updateActiveProducts({
          companyId,
          payload: {
            identifier: "retention-offer",
          },
        });
        showToast({
          message: `${retentionOfferDuration}-day trial is added to your account`,
          type: "success",
        });
        navigateToProjects({ companyId });
      } catch (error) {
        handleErrorWithToast({
          id: `SubscriptionExpired-updateActiveProducts-${Date.now().toString()}`,
          title:
            "Failed to apply retention offer. Please try again or reload the page",
          error,
        });
      }
    }
  }

  return {
    canApplyRetentionOffer,
    subtitle: getSubtitle(),
    retentionOfferDuration,
    onRetentionRequested,
    isLoading,
  };
}
